<template>
  <section
    class="data-table"
    :aria-label="title"
    :aria-busy="loading"
  >
    <header class="data-table__header">
      <div>
        <h2>{{ title }}</h2>
        <p v-if="description">{{ description }}</p>
      </div>

      <slot name="header"/>
    </header>

    <table-toolbar
      :search="searchText"
      :page-size="pageSize"
      :placeholder="searchPlaceholder"
      @search="setSearch($event)"
      @page-size="setPageSize($event)"
    >
      <slot name="filters"/>
    </table-toolbar>

    <div
      v-if="loading"
      class="data-table__state"
      role="status"
    >
      Загружаем данные…
    </div>

    <div
      v-else-if="error"
      class="data-table__state"
      role="alert"
    >
      <strong>{{ error }}</strong>
      <button
        type="button"
        @click="$emit('retry')"
      >
        Попробовать снова
      </button>
    </div>

    <div
      v-else-if="!filteredRows.length"
      class="data-table__state"
      role="status"
    >
      <Search :size="28" />

      <strong>{{ getTableStatusText }}</strong>
      <span>{{ getTableStatusDescription }}</span>
    </div>

    <div
      v-else class="data-table__scroll"
      tabindex="0"
      aria-label="Таблица данных, прокручивается по горизонтали"
    >
      <table>
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              scope="col"
              :aria-sort="getAriaSort(column)"
            >
              <button
                v-if="isSortableColumn(column)"
                type="button"
                @click="sort(column.key)"
              >
                {{ column.label }}
                <ArrowUpDown :size="13" />
              </button>

              <span v-else>{{ column.label }}</span>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="row in visibleRows"
            :key="row[rowKey]"
          >
            <td
              v-for="column in columns"
              :key="column.key"
            >
              <slot
                :name="`cell-${column.key}`"
                :row="row"
                :value="row[column.key]"
              >
                {{ getCellValue(row, column) }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <table-pagination
      v-if="isVisiblePagination"
      :page="currentPage"
      :page-size="pageSize"
      :total="filteredRows.length"
      @change="changePage($event)"
    />
  </section>
</template>

<script>
import {
  ArrowUpDown,
  Search
} from 'lucide-vue'

import TableToolbar from './components/tableToolbar'
import TablePagination from './components/tablePagination'

export default {
  name: 'DataTableV2',
  components: {
    ArrowUpDown,
    Search,
    TableToolbar,
    TablePagination
  },
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    columns: {
      type: Array,
      required: true
    },
    resetPageOnRowsChange: {
      type: Boolean,
      default: true
    },
    rowKey: {
      type: String,
      default: 'id'
    },
    searchKeys: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      default: ''
    },
    searchPlaceholder: {
      type: String,
      default: 'Поиск…'
    },
    emptyText: {
      type: String,
      default: 'Записей пока нет'
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      searchText: '',
      page: 1,
      pageSize: 25,
      sortKey: '',
      sortDirection: 1
    }
  },
  computed: {
    getTableStatusText () {
      return this.searchText
        ? 'Ничего не найдено'
        : this.emptyText
    },
    getTableStatusDescription () {
      return this.searchText
        ? 'Измените запрос или сбросьте фильтры.'
        : 'Новые записи появятся здесь.'
    },
    filteredRows () {
      const words = this.searchText.trim().toLocaleLowerCase('ru').split(/\s+/).filter(Boolean)
      const keys = this.searchKeys.length
        ? this.searchKeys
        : this.columns.map(({ key }) => key)

      const rows = this.rows.filter(row => {
        const text = keys.map(key => row[key] == null
          ? ''
          : row[key]
        ).join(' ').toLocaleLowerCase('ru')

        return words.every(word => text.includes(word))
      })

      if (!this.sortKey) {
        return rows
      }

      return rows.sort((a, b) => {
        const left = a[this.sortKey]
        const right = b[this.sortKey]
        const result = typeof left === 'number' && typeof right === 'number'
          ? left - right
          : String(left == null ? '' : left).localeCompare(String(right == null ? '' : right), 'ru', { numeric: true })

        return result * this.sortDirection
      })
    },
    currentPage () {
      return Math.min(this.page, Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize)))
    },
    visibleRows () {
      return this.filteredRows.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize)
    },
    isVisiblePagination () {
      return !this.loading && !this.error
    }
  },
  watch: {
    searchText () {
      this.page = 1
    },
    pageSize() {
      this.page = 1
    },
    rows() {
      this.page = this.resetPageOnRowsChange ? 1 : this.currentPage
    }
  },
  methods: {
    setSearch (event) {
      this.searchText = event
    },
    setPageSize (event) {
      this.pageSize = event
    },
    sort (key) {
      this.sortDirection = this.sortKey === key ? -this.sortDirection : 1
      this.sortKey = key
      this.page = 1
    },
    getAriaSort (column) {
      const var1 = this.sortDirection === 1 ? 'ascending' : 'descending'
      const var2 = column.sortable === false ? null : 'none'

      return this.sortKey === column.key ? var1 : var2
    },
    isSortableColumn (column) {
      return column.sortable !== false
    },
    getCellValue (row, column) {
      const isCellVoid = row[column.key] == null || row[column.key] === ''

      return isCellVoid ? '—' : row[column.key]
    },
    changePage (event) {
      this.page = event
    }
  }
}
</script>

<style scoped lang="scss" src="./style.scss"/>
